import React, { useState, useEffect, useMemo } from 'react';
import { clientApi, formatApiError } from '../../context/ClientAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Plus, Trash, Eye, PencilSimple, FileText, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useClientAuth } from '../../context/ClientAuthContext';

export default function ClientInvoices() {
  const { client: clientUser } = useClientAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptyForm = {
    customer_name: '',
    customer_email: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    items: [{ _key: '1', description: '', quantity: 1, unit_price: 0 }],
    vat_type: '',
    tax_rate: 0,
    notes: '',
  };
  const [form, setForm] = useState(emptyForm);

  const vatOptions = [
    { label: 'Select VAT rate', value: '', rate: 0 },
    { label: 'Standard VAT (20%)', value: '20', rate: 20 },
    { label: 'Reduced VAT (5%)', value: '5', rate: 5 },
    { label: 'Zero Rate (0%)', value: 'zero', rate: 0 },
    { label: 'No VAT', value: 'none', rate: 0 },
    { label: 'Exempt', value: 'exempt', rate: 0 },
    { label: 'Custom VAT', value: 'custom', rate: 0 },
  ];

  const handleVatTypeChange = (value) => {
    const option = vatOptions.find((o) => o.value === value);
    if (value === 'custom') {
      setForm({ ...form, vat_type: value });
    } else {
      setForm({ ...form, vat_type: value, tax_rate: option?.rate || 0 });
    }
  };

  useEffect(() => { fetchInvoices(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInvoices = async () => {
    try {
      const { data } = await clientApi.get('/sales-invoices');
      setInvoices(data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (items, taxRate) => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const taxAmount = subtotal * (taxRate / 100);
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total };
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index] = { ...newItems[index], [field]: field === 'description' ? value : Number(value) || 0 };
    setForm({ ...form, items: newItems });
  };

  const addItem = () => {
    setForm({ ...form, items: [...form.items, { _key: String(Date.now()), description: '', quantity: 1, unit_price: 0 }] });
  };

  const removeItem = (index) => {
    if (form.items.length <= 1) return;
    setForm({ ...form, items: form.items.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.invoice_number) {
      toast.error('Customer name and invoice number are required');
      return;
    }

    setSaving(true);
    const { subtotal, taxAmount, total } = calculateTotals(form.items, form.tax_rate);

    try {
      const { data } = await clientApi.post('/sales-invoices', {
        ...form,
        subtotal,
        tax_amount: taxAmount,
        total,
        vat_type: form.vat_type,
      });
      setInvoices([data, ...invoices]);
      setCreateOpen(false);
      setForm(emptyForm);
      toast.success('Invoice created successfully');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await clientApi.delete(`/sales-invoices/${id}`);
      setInvoices(invoices.filter((inv) => inv.id !== id));
      toast.success('Invoice deleted');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const { data } = await clientApi.put(`/sales-invoices/${id}`, { status: newStatus });
      setInvoices(invoices.map((inv) => (inv.id === id ? data : inv)));
      toast.success(`Invoice marked as ${newStatus}`);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDownloadPdf = async (inv) => {
    try {
      const response = await clientApi.post(`/sales-invoices/${inv.id}/download-pdf`, {}, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${inv.invoice_number || 'draft'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  const { subtotal, taxAmount, total } = calculateTotals(form.items, form.tax_rate);

  const statusColor = (status) => {
    switch (status) {
      case 'sent': return 'text-blue-600 bg-blue-50';
      case 'paid': return 'text-green-600 bg-green-50';
      case 'overdue': return 'text-red-600 bg-red-50';
      default: return 'text-amber-600 bg-amber-50';
    }
  };

  return (
    <div className="space-y-6" data-testid="client-invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-2 mb-1">Sales Invoices</h1>
          <p className="text-muted-foreground">Create and manage invoices for your clients</p>
        </div>
        <Dialog open={createOpen} onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) setForm(emptyForm);
        }}>
          <DialogTrigger asChild>
            <Button data-testid="create-invoice-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Sales Invoice</DialogTitle>
              <DialogDescription>Fill in the details to create an invoice for your client</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Customer Name *</Label>
                  <Input
                    value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    placeholder="Customer / Company name"
                    data-testid="invoice-customer-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Customer Email</Label>
                  <Input
                    type="email"
                    value={form.customer_email}
                    onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                    placeholder="customer@example.com"
                    data-testid="invoice-customer-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Invoice Number *</Label>
                  <Input
                    value={form.invoice_number}
                    onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                    placeholder="INV-001"
                    data-testid="invoice-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Invoice Date</Label>
                  <Input
                    type="date"
                    value={form.invoice_date}
                    onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                    data-testid="invoice-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={form.due_date}
                    onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                    data-testid="invoice-due-date-input"
                  />
                </div>
              </div>

              {/* Line Items */}
              <div className="space-y-2">
                <Label>Line Items</Label>
                <div className="border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead className="w-[45%]">Description</TableHead>
                        <TableHead className="w-[15%]">Qty</TableHead>
                        <TableHead className="w-[20%]">Unit Price</TableHead>
                        <TableHead className="w-[15%] text-right">Amount</TableHead>
                        <TableHead className="w-[5%]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {form.items.map((item, idx) => (
                        <TableRow key={item._key || idx}>
                          <TableCell className="py-1 px-2">
                            <Input
                              value={item.description}
                              onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                              placeholder="Item description"
                              className="border-0 shadow-none h-8"
                              data-testid={`item-description-${idx}`}
                            />
                          </TableCell>
                          <TableCell className="py-1 px-2">
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                              className="border-0 shadow-none h-8"
                              data-testid={`item-quantity-${idx}`}
                            />
                          </TableCell>
                          <TableCell className="py-1 px-2">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.unit_price}
                              onChange={(e) => handleItemChange(idx, 'unit_price', e.target.value)}
                              className="border-0 shadow-none h-8"
                              data-testid={`item-price-${idx}`}
                            />
                          </TableCell>
                          <TableCell className="text-right text-sm py-1 px-2">
                            {formatCurrency(item.quantity * item.unit_price)}
                          </TableCell>
                          <TableCell className="py-1 px-2">
                            {form.items.length > 1 && (
                              <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeItem(idx)}>
                                <Trash className="w-3 h-3 text-destructive" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>

              {/* VAT & Totals */}
              <div className="flex justify-end">
                <div className="w-72 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{formatCurrency(subtotal)}</span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Select value={form.vat_type} onValueChange={handleVatTypeChange}>
                        <SelectTrigger className="h-8 text-sm" data-testid="vat-type-select">
                          <SelectValue placeholder="Select VAT rate" />
                        </SelectTrigger>
                        <SelectContent>
                          {vatOptions.filter(o => o.value).map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {form.vat_type === 'custom' && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground">Rate:</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.1"
                          value={form.tax_rate}
                          onChange={(e) => setForm({ ...form, tax_rate: Number(e.target.value) || 0 })}
                          className="w-20 h-7 text-sm"
                          data-testid="custom-vat-input"
                        />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        VAT {form.vat_type === 'exempt' ? '(Exempt)' : form.vat_type === 'none' ? '(N/A)' : `(${form.tax_rate}%)`}
                      </span>
                      <span>{formatCurrency(taxAmount)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between font-semibold border-t pt-2">
                    <span>Total</span>
                    <span data-testid="invoice-total">{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes</Label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Payment terms, bank details, or any additional notes..."
                  className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="invoice-notes"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} data-testid="save-invoice-btn">
                  {saving ? 'Saving...' : 'Create Invoice'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Invoice View Dialog */}
      <Dialog open={!!viewInvoice} onOpenChange={(open) => !open && setViewInvoice(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Invoice {viewInvoice?.invoice_number}</DialogTitle>
          </DialogHeader>
          {viewInvoice && (
            <div className="space-y-4 py-2" data-testid="invoice-view-detail">
              {/* From / To header */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">From</p>
                  <p className="font-semibold">{clientUser?.company_name || clientUser?.name || '-'}</p>
                  {clientUser?.company_name && clientUser?.name && <p className="text-muted-foreground">{clientUser.name}</p>}
                  {clientUser?.address && <p className="text-muted-foreground">{clientUser.address}</p>}
                  {clientUser?.email && <p className="text-muted-foreground">{clientUser.email}</p>}
                  {clientUser?.phone && <p className="text-muted-foreground">{clientUser.phone}</p>}
                </div>
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">To</p>
                  <p className="font-semibold">{viewInvoice.customer_name}</p>
                  {viewInvoice.customer_email && <p className="text-muted-foreground">{viewInvoice.customer_email}</p>}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm border-t pt-3">
                <div>
                  <p className="text-muted-foreground text-xs">Invoice #</p>
                  <p className="font-medium">{viewInvoice.invoice_number}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Date</p>
                  <p>{viewInvoice.invoice_date || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Due Date</p>
                  <p>{viewInvoice.due_date || '-'}</p>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Description</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Unit Price</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(viewInvoice.items || []).map((item, idx) => (
                    <TableRow key={`view-item-${idx}`}>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{formatCurrency(item.unit_price)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.quantity * item.unit_price)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex justify-end">
                <div className="w-64 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{formatCurrency(viewInvoice.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {viewInvoice.vat_type === 'exempt' ? 'VAT (Exempt)' 
                        : viewInvoice.vat_type === 'none' ? 'VAT (N/A)' 
                        : `VAT (${viewInvoice.tax_rate}%)`}
                    </span>
                    <span>{formatCurrency(viewInvoice.tax_amount)}</span>
                  </div>
                  <div className="flex justify-between font-semibold border-t pt-1">
                    <span>Total</span>
                    <span>{formatCurrency(viewInvoice.total)}</span>
                  </div>
                </div>
              </div>
              {viewInvoice.notes && (
                <div className="text-sm">
                  <p className="text-muted-foreground">Notes</p>
                  <p className="whitespace-pre-wrap">{viewInvoice.notes}</p>
                </div>
              )}
              <div className="flex justify-end pt-2 border-t">
                <Button onClick={() => handleDownloadPdf(viewInvoice)} data-testid="download-invoice-pdf-btn">
                  <DownloadSimple className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invoices List */}
      <Card className="border border-border">
        <CardHeader className="pb-4">
          <CardTitle className="heading-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Your Invoices
          </CardTitle>
          <CardDescription>{invoices.length} invoice{invoices.length !== 1 ? 's' : ''} created</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No invoices created yet</p>
              <p className="text-sm text-muted-foreground mt-1">Click "Create Invoice" to create your first sales invoice</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id} data-testid={`sales-invoice-row-${inv.id}`}>
                    <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                    <TableCell>{inv.customer_name}</TableCell>
                    <TableCell>{inv.invoice_date || '-'}</TableCell>
                    <TableCell>{inv.due_date || '-'}</TableCell>
                    <TableCell className="font-medium">{formatCurrency(inv.total)}</TableCell>
                    <TableCell>
                      <Badge className={statusColor(inv.status)}>{inv.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setViewInvoice(inv)} data-testid={`view-invoice-${inv.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownloadPdf(inv)} data-testid={`download-pdf-${inv.id}`} title="Download PDF">
                          <DownloadSimple className="w-4 h-4" />
                        </Button>
                        {inv.status === 'draft' && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleStatusChange(inv.id, 'sent')} data-testid={`send-invoice-${inv.id}`}>
                            <PencilSimple className="w-4 h-4 text-blue-600" />
                          </Button>
                        )}
                        {inv.status === 'sent' && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleStatusChange(inv.id, 'paid')} data-testid={`mark-paid-${inv.id}`}>
                            <Badge variant="outline" className="text-xs text-green-600 cursor-pointer">Paid</Badge>
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(inv.id)} data-testid={`delete-invoice-${inv.id}`}>
                          <Trash className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
